import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Settings, BarChart3, FileText, Code, Zap, Shield, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { CreateProjectModal } from './CreateProjectModal';
import { ProjectCard } from './ProjectCard';
import { QuickStats } from './QuickStats';
import { ThreeCards } from '../Common/ThreeCards';
import { logger } from '../../services/loggingService';
import { toast } from '../../lib/toast';
import { dbOperation, checkPermission } from '../../utils/database';
import { Heading, Text } from '../ui/Typography';
import { spacing } from '../../utils/spacing';

interface Project {
  id: string;
  name: string;
  description?: string;
  repository_url?: string;
  created_at: string;
  updated_at: string;
  blueprint_status?: 'draft' | 'locked' | 'executing' | 'complete';
  last_build_date?: string;
  compliance_score?: number;
}

interface QuickStat {
  title: string;
  value: number | string;
  change?: number;
  trend?: 'up' | 'down' | 'stable';
  icon: React.ComponentType<{ className?: string }>;
}

interface DashboardProps {
  onSelectProject: (projectId: string) => void;
  onDeleteProject: (projectId: string) => void;
  onProjectCreated?: (projectId: string) => void;
}

export function Dashboard({
  onSelectProject,
  onDeleteProject,
  onProjectCreated,
}: DashboardProps) {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [quickStats, setQuickStats] = useState<QuickStat[]>([]);
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    projectId: string | null;
    loading: boolean;
  }>({
    isOpen: false,
    projectId: null,
    loading: false,
  });
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadDashboardData();
      checkEmailVerification();
    }
  }, [user]);

  const checkEmailVerification = async () => {
    if (!user) return;

    try {
      // Add timeout to prevent hanging
      const getUserPromise = supabase.auth.getUser();
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Email check timeout')), 5000)
      );

      const {
        data: { user: authUser },
      } = await Promise.race([getUserPromise, timeoutPromise]).catch(() => ({
        data: { user: null },
      }));

      if (authUser) {
        setEmailVerified(!!authUser.email_confirmed_at);
      } else {
        setEmailVerified(false);
      }
    } catch (error) {
      logger.error('Error checking email verification', {
        error,
        userId: user?.id,
      });
      setEmailVerified(false);
    }
  };

  const loadDashboardData = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Load projects using standardized operation
      const projectsResult = await dbOperation<Project[]>(
        () => supabase
          .from('projects')
          .select(`
            id, name, description, repository_url, created_at, updated_at,
            blueprint_status,
            last_build_date,
            compliance_score
          `)
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false }),
        {
          operation: 'load projects',
          entity: 'projects',
          userId: user.id,
          showUserError: true
        }
      );

      if (!projectsResult.success) {
        setError(projectsResult.error || 'Failed to load projects');
        return;
      }

      const projectsData = projectsResult.data || [];
      setProjects(projectsData);

      // Load quick stats
      const stats = await loadQuickStats(user.id, projectsData);
      setQuickStats(stats);

      // Auto-select first project if none selected
      if (projectsData.length > 0 && !selectedProjectId) {
        setSelectedProjectId(projectsData[0].id);
        onSelectProject(projectsData[0].id);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data';
      setError(errorMessage);
      logger.error('Error loading dashboard data', { error: err, userId: user.id });
    } finally {
      setLoading(false);
    }
  };

  const loadQuickStats = async (userId: string, projects: Project[]) => {
    try {
      // Total projects
      const totalProjects = projects.length;

      // Active projects (blueprint locked or in execution)
      const activeProjects = projects.filter(p => 
        p.blueprint_status === 'locked' || p.blueprint_status === 'executing'
      ).length;

      // Compliance average
      const avgCompliance = projects.length > 0 
        ? Math.round((projects.reduce((sum, p) => sum + (p.compliance_score || 0), 0) / projects.length) * 10) / 10
        : 0;

      // Recent activity (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const recentActivity = projects.filter(p => 
        new Date(p.updated_at) > thirtyDaysAgo
      ).length;

      return [
        {
          title: 'Total Projects',
          value: totalProjects,
          icon: FileText,
          trend: 'stable',
        },
        {
          title: 'Active Builds',
          value: activeProjects,
          change: activeProjects - (projects.length * 0.3), // Simulated change
          trend: activeProjects > projects.length * 0.3 ? 'up' : 'down',
          icon: Zap,
        },
        {
          title: 'Avg Compliance',
          value: `${avgCompliance}%`,
          trend: 'up',
          icon: Shield,
        },
        {
          title: 'Recent Activity',
          value: recentActivity,
          change: recentActivity,
          trend: recentActivity > 0 ? 'up' : 'stable',
          icon: BarChart3,
        },
      ];
    } catch (error) {
      logger.error('Error loading quick stats', { error });
      return [];
    }
  };

  const handleCreateProject = async () => {
    setShowCreateModal(true);
  };

  const handleProjectCreated = async (projectId: string) => {
    setShowCreateModal(false);
    toast.success('Project created successfully!');
    
    // Refresh projects list
    await loadDashboardData();
    
    // Select the new project
    if (projectId && onProjectCreated) {
      setSelectedProjectId(projectId);
      onProjectCreated(projectId);
    }
  };

  const handleProjectSelected = (projectId: string) => {
    setSelectedProjectId(projectId);
    onSelectProject(projectId);
  };

  const handleDeleteProjectConfirm = async () => {
    if (!deleteConfirm.projectId || !user) return;

    setDeleteConfirm(prev => ({ ...prev, loading: true }));

    try {
      // Check permission first
      const hasPermission = await checkPermission(
        user.id,
        'project',
        'delete',
        deleteConfirm.projectId
      );

      if (!hasPermission) {
        toast.error('You do not have permission to delete this project');
        setDeleteConfirm({ isOpen: false, projectId: null, loading: false });
        return;
      }

      // Delete project using standardized operation
      const deleteResult = await dbOperation(
        () => supabase
          .from('projects')
          .delete()
          .eq('id', deleteConfirm.projectId)
          .eq('user_id', user.id),
        {
          operation: 'delete project',
          entity: 'projects',
          userId: user.id,
          projectId: deleteConfirm.projectId,
          showUserError: true
        }
      );

      if (!deleteResult.success) {
        logger.error('Failed to delete project', { 
          error: deleteResult.error, 
          projectId: deleteConfirm.projectId 
        });
        setDeleteConfirm(prev => ({ ...prev, loading: false }));
        return;
      }

      toast.success('Project deleted successfully');
      
      // Refresh projects
      await loadDashboardData();
      
      // Clear selection if deleted project was selected
      if (selectedProjectId === deleteConfirm.projectId) {
        setSelectedProjectId(null);
        onSelectProject('');
      }
      
      // Call parent callback  
      onDeleteProject(deleteConfirm.projectId);
      
      setDeleteConfirm({ isOpen: false, projectId: null, loading: false });
    } catch (error) {
      logger.error('Unexpected error deleting project', { error, projectId: deleteConfirm.projectId });
      toast.error('An unexpected error occurred');
      setDeleteConfirm(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteProjectCancel = () => {
    setDeleteConfirm({ isOpen: false, projectId: null, loading: false });
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-base p-4">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-accent-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-primary">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-base p-4">
        <div className="text-center max-w-md">
          <div className="text-error mb-4">
            <AlertCircle className="w-12 h-12 mx-auto" />
          </div>
          <h2 className="text-xl font-semibold text-primary mb-2">Something went wrong</h2>
          <p className="text-secondary mb-6">{error}</p>
          <button
            onClick={loadDashboardData}
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const hasProjects = projects.length > 0;

  return (
    <div className="h-full bg-base">
      {/* Page Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Heading level={1} className="text-primary">
              Dashboard
            </Heading>
            {hasProjects && (
              <div className="flex items-center gap-2 text-sm text-secondary">
                <span>{projects.length} project{projects.length !== 1 ? 's' : ''}</span>
                <span className="text-muted">•</span>
                <span>Compliance: {Math.round(projects.reduce((sum, p) => sum + (p.compliance_score || 0), 0) / Math.max(1, projects.length))}% avg</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {emailVerified === false && (
              <div className="text-xs text-error bg-red-50 dark:bg-red-900/20 px-3 py-1 rounded-full">
                Verify email to unlock all features
              </div>
            )}
            
            {hasProjects && (
              <button
                onClick={handleCreateProject}
                className="btn btn-primary"
                aria-label="Create new project"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        {!hasProjects ? (
          // Empty State
          <div className="text-center py-20">
            <FileText className="w-16 h-16 text-muted mx-auto mb-6" />
            <Heading level={2} className="mb-4">
              No projects yet
            </Heading>
            <Text className="mb-8 max-w-md mx-auto text-secondary">
              Get started by creating your first project. Cyclone will help you plan, build, and deploy your application with AI‑powered automation.
            </Text>
            <button
              onClick={handleCreateProject}
              className="btn btn-primary text-base"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Project
            </button>
          </div>
        ) : (
          <>
            {/* Quick Stats - Only show if projects exist */}
            {projects.length > 0 && (
              <div className="mb-8">
                <QuickStats stats={quickStats} />
              </div>
            )}

            {/* Three Cards - Show when project selected */}
            {selectedProjectId && (
              <div className="mb-8">
                <ThreeCards
                  plan={{
                    status: 'ready' as const,
                    summary: 'Blueprint locked and ready for execution',
                    details: 'All requirements mapped, tech stack validated',
                    phaseCount: 5, // Dynamic from project data
                  }}
                  cost={{
                    estimated: 12.50,
                    ceiling: 20.00,
                    status: 'under' as const,
                    currency: '£',
                  }}
                  risk={{
                    level: 'low' as const,
                    issues: ['Minor dependency update needed'],
                    mitigations: ['Auto-update in next cycle'],
                  }}
                  onViewDetails={(card) => {
                    // Handle card click - navigate to details
                    toast(`Viewing ${card} details`);
                  }}
                />
              </div>
            )}

            {/* Projects Grid */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-text">Your Projects</h2>
                  <p className="text-text-secondary">
                    Select a project to view details and start building
                  </p>
                </div>
                
                {hasProjects && (
                  <button
                    onClick={handleCreateProject}
                    className="inline-flex items-center px-4 py-2 bg-[hsl(var(--color-primary))] text-primary-foreground rounded-lg hover:bg-[hsl(var(--color-primary)/0.9)] transition-colors font-medium"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Project
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {projects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    isSelected={selectedProjectId === project.id}
                    onSelect={() => handleProjectSelected(project.id)}
                    onDelete={() => {
                      setDeleteConfirm({
                        isOpen: true,
                        projectId: project.id,
                        loading: false,
                      });
                    }}
                    onViewDetails={() => {
                      // Navigate to project details
                      handleProjectSelected(project.id);
                    }}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[var(--z-modal)]">
          <div className="bg-background rounded-lg p-6 max-w-md w-full max-w-sm">
            <div className="flex items-center mb-4">
              <Trash2 className="w-5 h-5 text-destructive mr-3" />
              <h3 className="text-lg font-semibold text-text">Delete Project</h3>
            </div>
            
            <p className="text-text-secondary mb-6">
              Are you sure you want to delete this project? This action cannot be undone and will permanently remove all associated data, blueprints, and build history.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteProjectCancel}
                className="px-4 py-2 text-text-secondary hover:text-text transition-colors"
                disabled={deleteConfirm.loading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProjectConfirm}
                className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors font-medium disabled:opacity-50"
                disabled={deleteConfirm.loading}
              >
                {deleteConfirm.loading ? 'Deleting...' : 'Delete Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleProjectCreated}
        />
      )}
    </div>
  );
}
